<template>
	<Teleport to="body">
		<div class="section-import-overlay" @pointerdown.stop @click.self="close">
			<section
				ref="dialogRef"
				class="section-import"
				role="dialog"
				aria-modal="true"
				:aria-labelledby="titleId"
				:aria-describedby="descriptionId"
				tabindex="-1"
				@keydown.esc="handleEscape"
				@keydown.tab="trapFocus"
			>
				<header class="section-import__header">
					<div>
						<h3 :id="titleId">Import {{ sectionLabel }}</h3>
						<p :id="descriptionId">
							Copy {{ sectionLabel }} from another {{ documentLabel }}. It replaces {{ sectionLabel }} in this {{ documentLabel }}.
						</p>
					</div>
					<button type="button" class="section-import__icon-button" aria-label="Close import" @click="close">×</button>
				</header>

				<div class="section-import__body">
					<div class="section-import__sources">
						<input
							ref="searchRef"
							v-model="query"
							type="search"
							class="section-import__search"
							placeholder="Search documents"
							:aria-label="`Search by reference, ${counterpartyWord} or property`"
						/>
						<p v-if="loading" class="section-import__status" role="status">Loading documents…</p>
						<div v-else-if="error" class="section-import__status section-import__status--error" role="alert">
							<p>{{ error }}</p>
							<button type="button" class="section-import__button" @click="$emit('retry')">Try again</button>
						</div>
						<p v-else-if="!sources.length" class="section-import__status">There is no other {{ documentLabel }} yet.</p>
						<p v-else-if="!filteredSources.length" class="section-import__status">Nothing matches the search.</p>
						<ul v-else ref="listRef" class="section-import__list" :aria-label="`Saved ${documentLabel} documents`">
							<li v-for="source in filteredSources" :key="source.key">
								<button
									type="button"
									class="section-import__source"
									:class="{ 'is-selected': source.key === selectedKey, 'is-muted': source.empty || source.same }"
									:aria-pressed="source.key === selectedKey ? 'true' : 'false'"
									@click="selectedKey = source.key"
								>
									<span class="section-import__source-top">
										<span class="section-import__reference">{{ source.reference || 'No reference' }}</span>
										<span class="section-import__date">{{ source.dateLabel }}</span>
									</span>
									<span v-if="sourceDetails(source)" class="section-import__source-meta">{{ sourceDetails(source) }}</span>
									<span v-if="source.editedLabel || sourceTags(source).length" class="section-import__tags">
										<span v-for="tag in sourceTags(source)" :key="tag" class="section-import__tag">{{ tag }}</span>
										<span v-if="source.editedLabel" class="section-import__edited">Edited {{ source.editedLabel }}</span>
									</span>
								</button>
							</li>
						</ul>
					</div>

					<div class="section-import__preview" aria-live="polite">
						<template v-if="selected">
							<div class="section-import__preview-header">
								<p class="section-import__preview-title">{{ sectionLabel }} in {{ selectedName }}</p>
								<p v-if="sourceDetails(selected)" class="section-import__source-meta">{{ sourceDetails(selected) }}</p>
							</div>
							<p v-if="selected.empty" class="section-import__status">{{ sectionLabel }} is empty in this {{ documentLabel }}.</p>
							<template v-else>
								<p v-if="selected.same" class="section-import__hint">This is already the same as in the {{ documentLabel }} you are editing.</p>
								<div v-for="(block, blockIndex) in selected.preview" :key="blockIndex" class="section-import__block">
									<h4 v-if="block.title">{{ block.title }}</h4>
									<p v-for="(paragraph, index) in block.paragraphs" :key="`paragraph-${index}`">{{ paragraph }}</p>
									<ul v-if="block.bullets.length">
										<li v-for="(bullet, index) in block.bullets" :key="`bullet-${index}`">{{ bullet }}</li>
									</ul>
									<dl v-if="block.rows.length">
										<div v-for="(row, index) in block.rows" :key="`row-${index}`">
											<dt>{{ row[0] }}</dt>
											<dd>{{ row[1] }}</dd>
										</div>
									</dl>
									<p v-if="block.note" class="section-import__note">{{ block.note }}</p>
								</div>
							</template>
						</template>
						<p v-else-if="!loading && !error && sources.length" class="section-import__status">
							Choose a {{ documentLabel }} to see its {{ sectionLabel }}.
						</p>
					</div>
				</div>

				<footer class="section-import__footer">
					<button type="button" class="section-import__button" @click="close">Cancel</button>
					<button
						type="button"
						class="section-import__button section-import__button--primary"
						:disabled="!canImport"
						@click="importSelected"
					>
						Import {{ sectionLabel }}
					</button>
				</footer>
			</section>
		</div>
	</Teleport>
</template>

<script setup>
import { computed, getCurrentInstance, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { sectionImportSearchText } from '@/utils/clientDocumentSectionImport';

const props = defineProps({
	sectionLabel: { type: String, required: true },
	// The kind of document, in lower case: "proposal", "quotation", "assignment confirmation".
	documentLabel: { type: String, required: true },
	// From buildSectionImportSources: { key, reference, dateLabel, counterparty, place, preview, empty, same, ... }.
	sources: { type: Array, default: () => [] },
	loading: { type: Boolean, default: false },
	error: { type: String, default: '' },
	// The element that opened the dialog; it gets the focus back when the dialog closes.
	returnFocus: { type: Object, default: null },
});

const emit = defineEmits(['import', 'retry', 'close']);

const instanceId = getCurrentInstance()?.uid ?? Math.random().toString(36).slice(2);
const titleId = `section-import-${instanceId}-title`;
const descriptionId = `section-import-${instanceId}-description`;
const dialogRef = ref(null);
const searchRef = ref(null);
const listRef = ref(null);
const query = ref('');
const selectedKey = ref('');

const counterpartyWord = computed(() =>
	props.documentLabel === 'assignment confirmation' ? 'subcontractor' : 'client',
);

const filteredSources = computed(() => {
	const words = query.value.toLowerCase().split(/\s+/).filter(Boolean);
	if (!words.length) return props.sources;
	return props.sources.filter((source) => {
		const haystack = sectionImportSearchText(source);
		return words.every((word) => haystack.includes(word));
	});
});

const selected = computed(() => props.sources.find((source) => source.key === selectedKey.value) || null);
const selectedName = computed(() => {
	if (selected.value?.reference) return selected.value.reference;
	return selected.value?.dateLabel
		? `the ${props.documentLabel} of ${selected.value.dateLabel}`
		: `the selected ${props.documentLabel}`;
});
const canImport = computed(() => Boolean(selected.value && !selected.value.empty && !selected.value.same));

// The first document worth copying from is chosen up front, so its preview shows straight away,
// and the list scrolls to it when it is further down.
watch(
	filteredSources,
	(sources) => {
		if (sources.some((source) => source.key === selectedKey.value)) return;
		selectedKey.value = (sources.find((source) => !source.empty && !source.same) || sources[0])?.key || '';
		nextTick(() => listRef.value?.querySelector('.is-selected')?.scrollIntoView?.({ block: 'center' }));
	},
	{ immediate: true },
);

const sourceDetails = (source) => [source.counterparty, source.place].filter(Boolean).join(' · ');

const sourceTags = (source) =>
	[
		source.empty && 'Empty',
		source.same && 'Same as now',
		source.origin === 'supplier' && 'Supplier documents',
		source.archived && 'Archived',
	].filter(Boolean);

const close = () => emit('close');

const importSelected = () => {
	if (canImport.value) emit('import', selected.value.key);
};

const handleEscape = (event) => {
	if (event?.isComposing) return;
	event?.preventDefault?.();
	event?.stopPropagation?.();
	close();
};

const trapFocus = (event) => {
	const focusable = Array.from(
		dialogRef.value?.querySelectorAll('button:not(:disabled), input:not(:disabled), [tabindex]:not([tabindex="-1"])') || [],
	);
	if (!focusable.length) {
		event.preventDefault();
		dialogRef.value?.focus?.();
		return;
	}
	const first = focusable[0];
	const last = focusable[focusable.length - 1];
	if (document.activeElement === dialogRef.value) {
		event.preventDefault();
		(event.shiftKey ? last : first).focus?.();
	} else if (event.shiftKey && document.activeElement === first) {
		event.preventDefault();
		last.focus?.();
	} else if (!event.shiftKey && document.activeElement === last) {
		event.preventDefault();
		first.focus?.();
	}
};

// The page behind stays still and out of reach while the dialog is open.
const modalEnvironment = { bodyOverflow: null, appRoot: null, appRootWasInert: false };

const lockEnvironment = () => {
	if (modalEnvironment.bodyOverflow !== null) return;
	modalEnvironment.bodyOverflow = document.body.style.overflow;
	document.body.style.overflow = 'hidden';
	const appRoot = document.getElementById('app');
	if (appRoot && !appRoot.contains(dialogRef.value)) {
		modalEnvironment.appRoot = appRoot;
		modalEnvironment.appRootWasInert = Boolean(appRoot.inert);
		appRoot.inert = true;
	}
};

const unlockEnvironment = () => {
	if (modalEnvironment.bodyOverflow === null) return;
	document.body.style.overflow = modalEnvironment.bodyOverflow;
	modalEnvironment.bodyOverflow = null;
	if (modalEnvironment.appRoot) {
		modalEnvironment.appRoot.inert = modalEnvironment.appRootWasInert;
		modalEnvironment.appRoot = null;
	}
};

onMounted(() => {
	lockEnvironment();
	nextTick(() => (searchRef.value || dialogRef.value)?.focus?.({ preventScroll: true }));
});

onUnmounted(() => {
	unlockEnvironment();
	const target = props.returnFocus;
	nextTick(() => target?.focus?.({ preventScroll: true }));
});
</script>

<style scoped>
.section-import-overlay {
	position: fixed;
	z-index: 1400;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 16px;
	background: rgba(15, 23, 42, 0.46);
	box-sizing: border-box;
}

.section-import {
	display: flex;
	flex-direction: column;
	width: min(920px, calc(100vw - 32px));
	height: min(640px, calc(100vh - 32px));
	border: 1px solid var(--border-color, #e5e7eb);
	border-radius: 12px;
	outline: none;
	background: var(--noc-background, #fff);
	box-shadow: 0 22px 60px rgba(15, 23, 42, 0.24);
	color: var(--text-color, #333);
	overflow: hidden;
}

.section-import__header {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 20px;
	padding: 22px 22px 16px;
	border-bottom: 1px solid var(--border-color, #e5e7eb);
}

.section-import__header h3 {
	margin: 0;
	font-size: 1.15rem;
	font-weight: 650;
}

.section-import__header p {
	margin: 5px 0 0;
	color: var(--text-color-secondary, #6b7280);
	font-size: 0.84rem;
	line-height: 1.4;
}

.section-import__icon-button {
	display: inline-flex;
	flex: 0 0 32px;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	padding: 0;
	border: 0;
	border-radius: 6px;
	background: transparent;
	color: var(--text-color-secondary, #6b7280);
	font: inherit;
	font-size: 1.45rem;
	line-height: 1;
	cursor: pointer;
}

.section-import__icon-button:hover,
.section-import__icon-button:focus-visible {
	background: var(--noc-background-secondary, #f5f6f8);
	color: var(--text-color, #333);
	outline: none;
}

.section-import__body {
	display: grid;
	flex: 1 1 auto;
	grid-template-columns: minmax(260px, 340px) minmax(0, 1fr);
	min-height: 0;
}

.section-import__sources {
	display: flex;
	flex-direction: column;
	gap: 10px;
	min-height: 0;
	padding: 16px 0 16px 16px;
	border-right: 1px solid var(--border-color, #e5e7eb);
}

.section-import__search {
	width: calc(100% - 16px);
	height: 36px;
	padding: 0 12px;
	border: 1px solid var(--border-color, #d1d5db);
	border-radius: 8px;
	outline: none;
	background: var(--noc-background, #fff);
	color: var(--text-color, #333);
	font: inherit;
	font-size: 14px;
	box-sizing: border-box;
}

.section-import__search:hover {
	border-color: var(--border-color-active, #9ca3af);
}

.section-import__search:focus {
	border-color: var(--noc-blue, #1261c9);
}

.section-import__list {
	flex: 1 1 auto;
	min-height: 0;
	margin: 0;
	padding: 0 16px 0 0;
	overflow-y: auto;
	list-style: none;
}

.section-import__list li + li {
	margin-top: 4px;
}

.section-import__source {
	display: flex;
	flex-direction: column;
	gap: 3px;
	width: 100%;
	padding: 9px 12px;
	border: 1px solid transparent;
	border-radius: 8px;
	background: transparent;
	color: var(--text-color, #333);
	font: inherit;
	text-align: left;
	cursor: pointer;
	touch-action: manipulation;
}

.section-import__source:focus-visible {
	border-color: var(--noc-blue, #1261c9);
	outline: none;
}

.section-import__source.is-selected {
	border-color: var(--noc-blue, #1261c9);
	background: var(--noc-blue-light-1, rgba(18, 97, 201, 0.06));
}

.section-import__source.is-muted:not(.is-selected) .section-import__reference {
	color: var(--text-color-secondary, #6b7280);
}

.section-import__source-top {
	display: flex;
	align-items: baseline;
	justify-content: space-between;
	gap: 12px;
	min-width: 0;
}

.section-import__reference {
	min-width: 0;
	overflow: hidden;
	font-size: 0.87rem;
	font-weight: 600;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.section-import__date {
	flex: 0 0 auto;
	color: var(--text-color-secondary, #6b7280);
	font-size: 0.78rem;
}

.section-import__source-meta {
	display: block;
	min-width: 0;
	overflow: hidden;
	color: var(--text-color-secondary, #6b7280);
	font-size: 0.8rem;
	line-height: 1.35;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.section-import__tags {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 4px 6px;
	margin-top: 2px;
}

.section-import__edited {
	color: var(--text-color-secondary, #6b7280);
	font-size: 0.72rem;
	line-height: 1.5;
}

.section-import__tag {
	padding: 1px 6px;
	border-radius: 4px;
	background: var(--noc-background-secondary, #f5f6f8);
	color: var(--text-color-secondary, #6b7280);
	font-size: 0.72rem;
	font-weight: 600;
	line-height: 1.5;
}

.section-import__preview {
	min-height: 0;
	padding: 16px 22px 20px;
	overflow-y: auto;
	font-size: 0.87rem;
	line-height: 1.5;
}

.section-import__preview-header {
	margin-bottom: 12px;
	padding-bottom: 10px;
	border-bottom: 1px solid var(--border-color-secondary, #ebeef1);
}

.section-import__preview-title {
	margin: 0 0 2px;
	font-size: 0.95rem;
	font-weight: 650;
}

.section-import__preview-header .section-import__source-meta {
	white-space: normal;
}

.section-import__block + .section-import__block {
	margin-top: 14px;
}

.section-import__block h4 {
	margin: 0 0 4px;
	font-size: 0.87rem;
	font-weight: 650;
}

.section-import__block p {
	margin: 0 0 6px;
	white-space: pre-line;
	overflow-wrap: anywhere;
}

.section-import__block ul {
	margin: 0;
	padding-left: 18px;
}

.section-import__block li {
	overflow-wrap: anywhere;
}

.section-import__block dl {
	display: grid;
	gap: 4px;
	margin: 0;
}

.section-import__block dl > div {
	display: grid;
	grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
	gap: 12px;
}

.section-import__block dt {
	color: var(--text-color-secondary, #6b7280);
	overflow-wrap: anywhere;
}

.section-import__block dd {
	margin: 0;
	text-align: right;
	overflow-wrap: anywhere;
}

.section-import__note,
.section-import__hint {
	color: var(--text-color-secondary, #6b7280);
	font-size: 0.8rem;
}

.section-import__hint {
	margin: 0 0 12px;
}

.section-import__status {
	margin: 0;
	padding-right: 16px;
	color: var(--text-color-secondary, #6b7280);
	font-size: 0.84rem;
}

.section-import__status--error p {
	margin: 0 0 10px;
	color: #b42318;
}

.section-import__footer {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 8px;
	padding: 14px 22px;
	border-top: 1px solid var(--border-color, #e5e7eb);
	background: var(--noc-background-secondary, #fafafb);
}

.section-import__button {
	min-height: 36px;
	padding: 7px 14px;
	border: 1px solid var(--border-color, #d1d5db);
	border-radius: 7px;
	background: var(--noc-background, #fff);
	color: var(--text-color, #333);
	font: inherit;
	font-size: 0.84rem;
	font-weight: 600;
	cursor: pointer;
	touch-action: manipulation;
}

.section-import__button:hover:not(:disabled),
.section-import__button:focus-visible {
	background: var(--noc-background-secondary, #f5f6f8);
	outline: none;
}

.section-import__button--primary {
	border-color: var(--primary-color, #1261c9);
	background: var(--primary-color, #1261c9);
	color: #fff;
}

.section-import__button--primary:hover:not(:disabled),
.section-import__button--primary:focus-visible {
	background: var(--primary-color-hover, #0d4fa7);
}

.section-import__button:disabled {
	opacity: 0.5;
	cursor: default;
}

@media (hover: hover) {
	.section-import__source:not(.is-selected):hover {
		background: var(--noc-background-secondary, #f5f6f8);
	}
}

@media (max-width: 700px) {
	.section-import__body {
		grid-template-columns: minmax(0, 1fr);
		grid-template-rows: minmax(0, 45%) minmax(0, 1fr);
	}

	.section-import__sources {
		border-right: 0;
		border-bottom: 1px solid var(--border-color, #e5e7eb);
	}
}
</style>
